/**
 * TherapistChat Component
 *
 * Main chat interface for conversations with the AI therapist.
 * Displays message history with markdown rendering, handles user input,
 * and provides streaming responses for real-time feedback.
 * Features a modern chat UI with rounded bubbles and textarea input for multiline messages.
 * Includes skeleton loading, error states with retry, and AlertDialog confirmation for
 * clearing chat history.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { shouldSendOnEnter } from "../../lib/composerKeys";
import { useStickyScroll } from "../../hooks/useStickyScroll";
import {
  Loader2,
  Send,
  Trash2,
  User,
  HeartHandshake,
  Sparkles,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChatMarkdown } from "./ChatMarkdown";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import * as chatService from "@/services/chat";
import type {
  ChatMessageResponse,
  ConversationChatResponse,
} from "@/types/chat";

interface TherapistChatProps {
  reflectionId: string;
}

/**
 * Message bubble component with optional markdown rendering
 */
function MessageBubble({
  content,
  isUser,
  isStreaming = false,
}: {
  content: string;
  isUser: boolean;
  isStreaming?: boolean;
}) {
  return (
    <div
      className={`
        max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed
        ${isUser ? "bg-primary text-primary-foreground rounded-tr-none" : "bg-muted text-foreground rounded-tl-none"}
      `}
    >
      {isUser ? (
        <div className="whitespace-pre-wrap">{content}</div>
      ) : (
        <div className="prose prose-sm max-w-none dark:prose-invert [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:my-2 [&>ol]:my-2 [&>li]:my-0.5">
          <ChatMarkdown>{content}</ChatMarkdown>
          {isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-current animate-pulse ml-0.5 align-middle" />
          )}
        </div>
      )}
    </div>
  );
}

export function TherapistChat({ reflectionId }: TherapistChatProps) {
  const [input, setInput] = useState("");
  // Optimistic user message state
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(
    null
  );
  // Streaming response state
  const [streamingContent, setStreamingContent] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  // Fetch or create chat
  const {
    data: chat,
    isLoading,
    error,
  } = useQuery<ConversationChatResponse>({
    queryKey: ["chat", reflectionId],
    queryFn: async () => {
      try {
        return await chatService.getChat(reflectionId);
      } catch (err) {
        // If chat doesn't exist, create it
        return await chatService.createOrGetChat(reflectionId);
      }
    },
  });

  // Delete chat mutation
  const deleteChatMutation = useMutation({
    mutationFn: () => chatService.deleteChat(reflectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat", reflectionId] });
    },
  });

  // Follow the stream only while the user is already at the bottom. This used
  // to scroll unconditionally, so reading back mid-response was impossible.
  const { containerRef, handleScroll } = useStickyScroll([
    chat?.messages,
    pendingUserMessage,
    streamingContent,
  ]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 150); // Max 150px height
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  const handleSendMessage = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = input.trim();
    setInput("");
    setPendingUserMessage(userMessage);
    setStreamingContent("");
    setIsStreaming(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    try {
      await chatService.sendMessageStreaming(
        reflectionId,
        { content: userMessage },
        {
          onUserMessage: (msg: ChatMessageResponse) => {
            // User message saved, clear pending
            setPendingUserMessage(null);
            // Optimistically add to cache
            queryClient.setQueryData<ConversationChatResponse>(
              ["chat", reflectionId],
              (old) => {
                if (!old) return old;
                return {
                  ...old,
                  messages: [...old.messages, msg],
                };
              }
            );
          },
          onContent: (content: string) => {
            setStreamingContent((prev) => prev + content);
          },
          onDone: (msg: ChatMessageResponse) => {
            // Add assistant message to cache FIRST, before clearing streaming content
            // This prevents the message from "disappearing" during the transition
            queryClient.setQueryData<ConversationChatResponse>(
              ["chat", reflectionId],
              (old) => {
                if (!old) return old;
                return {
                  ...old,
                  messages: [...old.messages, msg],
                };
              }
            );
            // Clear streaming state AFTER the cache is updated
            // Use setTimeout to ensure React has processed the cache update
            setTimeout(() => {
              setIsStreaming(false);
              setStreamingContent("");
            }, 0);
          },
          onError: (errorMsg: string) => {
            setIsStreaming(false);
            setStreamingContent("");
            setPendingUserMessage(null);
            // Restore input on error
            setInput(userMessage);
            console.error("Streaming error:", errorMsg);
            toast.error("Failed to send message. Please try again.");
          },
        }
      );
    } catch (err) {
      setIsStreaming(false);
      setStreamingContent("");
      setPendingUserMessage(null);
      // Restore input on error
      setInput(userMessage);
      console.error("Failed to send message:", err);
      toast.error("Connection error. Please try again.");
    }
  }, [input, isStreaming, reflectionId, queryClient]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter without Shift (Shift+Enter for new line)
    if (shouldSendOnEnter(e)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-96 p-4 space-y-4">
        {/* Skeleton message bubbles */}
        <div className="flex gap-3">
          <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
          <div className="space-y-2 flex-1 max-w-[70%]">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
        <div className="flex gap-3 flex-row-reverse">
          <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
          <div className="space-y-2 max-w-[60%]">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
        <div className="flex gap-3">
          <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
          <div className="space-y-2 flex-1 max-w-[70%]">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 p-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-medium text-destructive">
            Failed to load chat
          </p>
          <p className="text-xs text-muted-foreground">
            {error instanceof Error ? error.message : "Please try again."}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              queryClient.invalidateQueries({
                queryKey: ["chat", reflectionId],
              })
            }
            className="gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const hasMessages = chat && chat.messages.length > 0;
  const showStreamingMessage = isStreaming && streamingContent;

  return (
    <div className="flex flex-col h-[calc(100vh-130px)] md:h-[calc(100vh-250px)] md:max-h-[800px] relative">
      {/* Chat header with therapy lens badge */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        {/* Therapy Lens Badge */}
        {chat?.therapy_style ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-full">
            <Sparkles className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
              {chat.therapy_style.name}
            </span>
          </div>
        ) : (
          <div />
        )}

        {/* Clear chat button with confirmation dialog */}
        {hasMessages ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={deleteChatMutation.isPending || isStreaming}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Chat
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear chat history?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all messages in this
                  conversation. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteChatMutation.mutate()}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Clear Chat
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <div />
        )}
      </div>

      {/* Messages - extra padding at bottom for fixed input on mobile */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 pb-32 md:pb-4 space-y-4"
      >
        {!hasMessages && !pendingUserMessage && !showStreamingMessage ? (
          <div className="flex items-center justify-center h-full text-center text-muted-foreground p-8">
            <div>
              <HeartHandshake className="w-12 h-12 mx-auto mb-4 text-primary/50" />
              <p className="mb-2 font-medium">Start a conversation</p>
              <p className="text-sm">
                Share what&apos;s on your mind, explore patterns, or get
                guidance.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Saved messages */}
            {chat?.messages.map((message) => {
              const isUser = message.role === "user";
              return (
                <div
                  key={message.id}
                  className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}
                >
                  {/* Avatar */}
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                      ${isUser ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}
                    `}
                  >
                    {isUser ? (
                      <User className="w-5 h-5" />
                    ) : (
                      <HeartHandshake className="w-5 h-5" />
                    )}
                  </div>

                  {/* Message bubble */}
                  <MessageBubble content={message.content} isUser={isUser} />
                </div>
              );
            })}

            {/* Pending user message (optimistic UI) */}
            {pendingUserMessage && (
              <div className="flex gap-3 flex-row-reverse">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-primary text-primary-foreground">
                  <User className="w-5 h-5" />
                </div>
                <MessageBubble content={pendingUserMessage} isUser={true} />
              </div>
            )}

            {/* Streaming assistant response */}
            {showStreamingMessage && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <HeartHandshake className="w-5 h-5" />
                </div>
                <MessageBubble
                  content={streamingContent}
                  isUser={false}
                  isStreaming={true}
                />
              </div>
            )}

            {/* Loading indicator when waiting for stream to start */}
            {isStreaming && !streamingContent && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <HeartHandshake className="w-5 h-5" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-3 flex items-center">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input form - fixed at bottom on mobile, regular positioning on desktop */}
      <div className="fixed md:relative bottom-0 left-0 right-0 p-4 border-t bg-background md:bg-transparent z-50">
        <form
          onSubmit={handleSubmit}
          className="flex gap-2 max-w-3xl mx-auto items-end"
        >
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Share what's on your mind... (Shift+Enter for new line)"
            className="flex-1 min-h-[44px] max-h-[150px] py-2.5 px-4 resize-none rounded-2xl bg-muted/50 border border-input focus:outline-none focus:ring-2 focus:ring-ring focus:bg-background transition-colors"
            disabled={isStreaming}
            rows={1}
          />
          <Button
            type="submit"
            disabled={!input.trim() || isStreaming}
            className="rounded-full w-10 h-10 p-0 flex items-center justify-center flex-shrink-0"
          >
            {isStreaming ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground text-center mt-2 md:hidden">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
